import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar } from '../../ui/calendar';
import { CalendarIcon, Check, Pencil, Trash2 } from 'lucide-react';
import { format, addMonths, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
  getLoanScheduleSummaryLabel,
  getPayrollScheduleMeta,
  simulateLoanSchedule
} from '../../../utils/payrollLoanSchedule';

const LoansPage = ({
  loans,
  isLoading,
  filters,
  setFilters,
  branches,
  payrollEmployees,
  axiosInstance,
  refreshLoans,
  selectedLoanId,
  setSelectedLoanId
}) => {
  const safeLoans = Array.isArray(loans) ? loans : [];
  const safeBranches = Array.isArray(branches) ? branches : [];
  const safePayrollEmployees = Array.isArray(payrollEmployees) ? payrollEmployees : [];

  const [activeOverrides, setActiveOverrides] = React.useState({});

  const [isNewLoanOpen, setIsNewLoanOpen] = React.useState(false);
  const [editingLoanId, setEditingLoanId] = React.useState(null);
  const [employeeQuery, setEmployeeQuery] = React.useState('');
  const [selectedEmployee, setSelectedEmployee] = React.useState(null);
  const [amount, setAmount] = React.useState('');
  const [loanType, setLoanType] = React.useState('');
  const [loanSubtype, setLoanSubtype] = React.useState('');
  const [deductFrom, setDeductFrom] = React.useState('');
  const [deductPerCutoff, setDeductPerCutoff] = React.useState('');
  const [payrollFrequency, setPayrollFrequency] = React.useState('');
  const [payrollOnceMonthTarget, setPayrollOnceMonthTarget] = React.useState('');
  const [payrollDeduct15th, setPayrollDeduct15th] = React.useState('');
  const [payrollDeductEndOfMonth, setPayrollDeductEndOfMonth] = React.useState('');
  const [startDate, setStartDate] = React.useState(null);
  const [isCutoffModalOpen, setIsCutoffModalOpen] = React.useState(false);
  const [cutoffRows, setCutoffRows] = React.useState([]);
  const [detectedCutoffCount, setDetectedCutoffCount] = React.useState(0);
  const [remainingBalanceState, setRemainingBalanceState] = React.useState(null);
  const [remainingPeriodsState, setRemainingPeriodsState] = React.useState(null);
  const [totalDeductedState, setTotalDeductedState] = React.useState(null);
  const [projectedEndDateState, setProjectedEndDateState] = React.useState('');
  const autoDeactivatedRef = React.useRef({});

  const filteredLoans = React.useMemo(() => {
    const employeeName = String(filters?.employeeName || '').trim().toLowerCase();
    const branch = String(filters?.branch || 'all').trim();
    const government = Boolean(filters?.government);
    const company = Boolean(filters?.company);

    return safeLoans.filter((loan) => {
      if (employeeName) {
        const name = String(loan?.employee_name || '').toLowerCase();
        if (!name.includes(employeeName)) return false;
      }

      if (branch && branch !== 'all') {
        if (String(loan?.branch || '') !== branch) return false;
      }

      const type = String(loan?.loan_type || '').toLowerCase();
      const typeFilterActive = government || company;
      if (typeFilterActive) {
        const allowed = new Set();
        if (government) allowed.add('government');
        if (company) allowed.add('company');
        if (!allowed.has(type)) return false;
      }

      return true;
    });
  }, [safeLoans, filters]);

  const selectedLoan = React.useMemo(() => {
    if (!selectedLoanId) return null;
    return safeLoans.find((loan) => String(loan?.id) === String(selectedLoanId)) || null;
  }, [safeLoans, selectedLoanId]);

  const selectedLoanIsActive = React.useMemo(() => {
    if (!selectedLoan?.id) return true;
    const key = String(selectedLoan.id);
    if (Object.prototype.hasOwnProperty.call(activeOverrides, key)) return Boolean(activeOverrides[key]);
    if (typeof selectedLoan.is_active === 'boolean') return selectedLoan.is_active;
    return true;
  }, [activeOverrides, selectedLoan]);

  React.useEffect(() => {
    if (!selectedLoanId) return;
    const isSelectedStillVisible = filteredLoans.some((loan) => String(loan?.id) === String(selectedLoanId));
    if (!isSelectedStillVisible) setSelectedLoanId(null);
  }, [filteredLoans, selectedLoanId, setSelectedLoanId]);

  const handleToggleActive = React.useCallback(
    async (nextValue) => {
      if (!selectedLoan?.id) return;
      if (!axiosInstance) {
        toast.error('Missing API client');
        return;
      }

      const loanId = String(selectedLoan.id);
      const prevValue = selectedLoanIsActive;
      setActiveOverrides((prev) => ({ ...(prev || {}), [loanId]: Boolean(nextValue) }));

      try {
        const res = await axiosInstance.patch(`/payroll/loans/${loanId}/status`, { is_active: Boolean(nextValue) });
        const updated = res?.data;
        if (updated?.id) {
          setActiveOverrides((prev) => ({ ...(prev || {}), [String(updated.id)]: Boolean(updated.is_active) }));
        }
        if (typeof refreshLoans === 'function') {
          try { await refreshLoans(); } catch {}
        }
      } catch (error) {
        setActiveOverrides((prev) => ({ ...(prev || {}), [loanId]: prevValue }));
        toast.error('Failed to update loan status');
      }
    },
    [axiosInstance, refreshLoans, selectedLoan, selectedLoanIsActive]
  );

  const setEmployeeName = (value) => {
    setFilters((prev) => ({ ...(prev || {}), employeeName: value }));
  };

  const toggleGovernment = () => {
    setFilters((prev) => ({ ...(prev || {}), government: !prev?.government }));
  };

  const toggleCompany = () => {
    setFilters((prev) => ({ ...(prev || {}), company: !prev?.company }));
  };

  const setBranch = (value) => {
    setFilters((prev) => ({ ...(prev || {}), branch: value }));
  };

  const clearFilters = async () => {
    setFilters({
      employeeName: '',
      branch: 'all',
      government: false,
      company: false
    });
    if (typeof refreshLoans === 'function') {
      try { await refreshLoans(); } catch {}
    }
  };

  const governmentActive = Boolean(filters?.government);
  const companyActive = Boolean(filters?.company);

  const sanitizeNumericInput = (value) => {
    const raw = String(value ?? '');
    let out = '';
    let dotUsed = false;
    for (const ch of raw) {
      if (ch >= '0' && ch <= '9') {
        out += ch;
        continue;
      }
      if (ch === '.' && !dotUsed) {
        out += ch;
        dotUsed = true;
      }
    }
    return out;
  };

  const amountNumber = React.useMemo(() => {
    const x = parseFloat(String(amount || '').trim());
    return Number.isFinite(x) ? x : 0;
  }, [amount]);

  const deductPerCutoffNumber = React.useMemo(() => {
    const x = parseFloat(String(deductPerCutoff || '').trim());
    return Number.isFinite(x) ? x : 0;
  }, [deductPerCutoff]);

  const payrollDeduct15thNumber = React.useMemo(() => {
    const x = parseFloat(String(payrollDeduct15th || '').trim());
    return Number.isFinite(x) ? x : 0;
  }, [payrollDeduct15th]);

  const payrollDeductEndOfMonthNumber = React.useMemo(() => {
    const x = parseFloat(String(payrollDeductEndOfMonth || '').trim());
    return Number.isFinite(x) ? x : 0;
  }, [payrollDeductEndOfMonth]);

  const isMfSelected = deductFrom === 'mf';
  const isPayrollSelected = deductFrom === 'payroll';
  const isPayrollOnceMonth = isPayrollSelected && payrollFrequency === 'once_month';
  const isPayrollTwiceMonth = isPayrollSelected && payrollFrequency === 'twice_month';

  const payrollSchedulePreview = React.useMemo(() => {
    return simulateLoanSchedule({
      amount: amountNumber,
      deductFrom,
      startDate,
      deductPerCutoff: deductPerCutoffNumber,
      payrollFrequency,
      payrollOnceMonthTarget,
      payrollDeduct15th: payrollDeduct15thNumber,
      payrollDeductEndOfMonth: payrollDeductEndOfMonthNumber
    });
  }, [
    amountNumber,
    deductFrom,
    startDate,
    deductPerCutoffNumber,
    payrollFrequency,
    payrollOnceMonthTarget,
    payrollDeduct15thNumber,
    payrollDeductEndOfMonthNumber
  ]);

  const cutoffCount = React.useMemo(() => {
    if (isPayrollSelected) return Number(payrollSchedulePreview.cutoffCount) || 0;
    if (amountNumber <= 0 || deductPerCutoffNumber <= 0) return 0;
    return Math.ceil(amountNumber / deductPerCutoffNumber);
  }, [amountNumber, deductPerCutoffNumber, isPayrollSelected, payrollSchedulePreview.cutoffCount]);

  const toCutoffLabel = (value) => {
    if (!Number.isFinite(value) || value <= 0) return '0';
    return String(Math.round(value));
  };

  const formatDateLabel = (value) => {
    if (!value) return '-';
    try {
      return format(parseISO(String(value)), 'MM/dd/yyyy');
    } catch {
      return '-';
    }
  };

  const formatMoneyLabel = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return `₱ ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatLoanTypeLabel = (loan) => {
    const t = String(loan?.loan_type || '').toLowerCase();
    const typeLabel = t ? `${t.charAt(0).toUpperCase()}${t.slice(1)}` : '';
    const subtype = String(loan?.loan_subtype || '').trim();
    if (!typeLabel && !subtype) return '-';
    if (typeLabel && subtype) return `${typeLabel} - ${subtype}`;
    return typeLabel || subtype;
  };

  const computedEndDate = React.useMemo(() => {
    if (!startDate) return null;
    if (!Number.isFinite(cutoffCount) || cutoffCount <= 0) return null;
    if (isPayrollSelected) return payrollSchedulePreview.endDate || null;
    if (!isMfSelected) return null;
    return addMonths(startDate, cutoffCount);
  }, [startDate, cutoffCount, isPayrollSelected, payrollSchedulePreview.endDate, isMfSelected]);

  const typeSubtypes = React.useMemo(() => {
    if (loanType === 'government') return ['Salary Loan', 'Calamity Loan', 'HDMF Loan'];
    if (loanType === 'company') return ['Cash Advance', 'Gadgets', 'Vehicle', 'Other'];
    return [];
  }, [loanType]);

  const employeeMatches = React.useMemo(() => {
    const q = String(employeeQuery || '').trim().toLowerCase();
    if (!q) return [];
    const matches = safePayrollEmployees.filter((emp) => {
      const name = String(emp?.full_name || '').toLowerCase();
      const id = String(emp?.employee_id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
    return matches.slice(0, 10);
  }, [employeeQuery, safePayrollEmployees]);

  const handleSelectEmployee = (emp) => {
    setSelectedEmployee(emp);
    setEmployeeQuery(emp?.full_name || '');
  };

  const resetLoanForm = React.useCallback(() => {
    setEditingLoanId(null);
    setSelectedEmployee(null);
    setEmployeeQuery('');
    setAmount('');
    setLoanType('');
    setLoanSubtype('');
    setDeductFrom('');
    setDeductPerCutoff('');
    setPayrollFrequency('');
    setPayrollOnceMonthTarget('');
    setPayrollDeduct15th('');
    setPayrollDeductEndOfMonth('');
    setStartDate(null);
  }, []);

  const closeLoanModal = React.useCallback(() => {
    setIsNewLoanOpen(false);
    resetLoanForm();
  }, [resetLoanForm]);

  const handleSelectDeductFrom = (value) => {
    setDeductFrom(value);
    if (value === 'mf') {
      setPayrollFrequency('');
      setPayrollOnceMonthTarget('');
      setPayrollDeduct15th('');
      setPayrollDeductEndOfMonth('');
      return;
    }
    if (value === 'payroll') {
      setPayrollFrequency('');
      setPayrollOnceMonthTarget('');
      setPayrollDeduct15th('');
      setPayrollDeductEndOfMonth('');
    }
  };

  const handleSelectPayrollFrequency = (value) => {
    setPayrollFrequency(value);
    if (value === 'once_month') {
      setPayrollDeduct15th('');
      setPayrollDeductEndOfMonth('');
      return;
    }
    if (value === 'twice_month') {
      setPayrollOnceMonthTarget('');
      setDeductPerCutoff('');
    }
  };

  const openNewLoan = () => {
    setIsNewLoanOpen(true);
    resetLoanForm();
  };

  const openEditLoan = React.useCallback(() => {
    if (!selectedLoan) return;
    const employeeId = String(selectedLoan.employee_id || '').trim();
    const employeeName = String(selectedLoan.employee_name || '').trim();
    const matchedEmployee = safePayrollEmployees.find((emp) => {
      const empId = String(emp?.employee_id || '').trim();
      const empName = String(emp?.full_name || '').trim().toLowerCase();
      return (employeeId && empId === employeeId) || (employeeName && empName === employeeName.toLowerCase());
    });
    const employee = matchedEmployee || {
      employee_id: employeeId,
      full_name: employeeName,
      branch: String(selectedLoan.branch || '').trim()
    };

    setEditingLoanId(String(selectedLoan.id));
    setSelectedEmployee(employee);
    setEmployeeQuery(employee.full_name || '');
    setAmount(String(selectedLoan.amount ?? ''));
    setLoanType(String(selectedLoan.loan_type || ''));
    setLoanSubtype(String(selectedLoan.loan_subtype || ''));
    setDeductFrom(String(selectedLoan.deduct_from || ''));
    setDeductPerCutoff(String(selectedLoan.deduct_per_cutoff ?? ''));
    setPayrollFrequency(String(selectedLoan.payroll_frequency || ''));
    setPayrollOnceMonthTarget(String(selectedLoan.payroll_once_month_target || ''));
    setPayrollDeduct15th(String(selectedLoan.payroll_deduct_15th ?? ''));
    setPayrollDeductEndOfMonth(String(selectedLoan.payroll_deduct_end_of_month ?? ''));
    setStartDate(selectedLoan.start_date ? parseISO(String(selectedLoan.start_date)) : null);
    setIsNewLoanOpen(true);
  }, [safePayrollEmployees, selectedLoan]);

  const handleDeleteLoan = React.useCallback(async () => {
    if (!selectedLoan?.id) return;
    if (!axiosInstance) {
      toast.error('Missing API client');
      return;
    }
    const confirmed = window.confirm(`Delete loan for ${selectedLoan.employee_name || 'this employee'}?`);
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/payroll/loans/${selectedLoan.id}`);
      toast.success('Loan deleted');
      setSelectedLoanId(null);
      if (typeof refreshLoans === 'function') {
        try { await refreshLoans(); } catch {}
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete loan');
    }
  }, [axiosInstance, refreshLoans, selectedLoan, setSelectedLoanId]);

  const handleSaveLoan = async () => {
    if (!axiosInstance) { toast.error('Missing API client'); return; }
    if (!selectedEmployee?.employee_id) { toast.error('Please select an employee'); return; }
    if (!(amountNumber > 0)) { toast.error('Please enter a valid amount'); return; }
    if (loanType !== 'government' && loanType !== 'company') { toast.error('Please select a loan type'); return; }
    if (!loanSubtype) { toast.error('Please select a subtype'); return; }
    if (deductFrom !== 'payroll' && deductFrom !== 'mf') { toast.error('Please select Deduct from'); return; }
    if (isMfSelected && !(deductPerCutoffNumber > 0)) { toast.error('Please enter a valid Deduct amount'); return; }
    if (isPayrollSelected && !payrollFrequency) { toast.error('Please select a Payroll frequency'); return; }
    if (isPayrollOnceMonth) {
      if (!payrollOnceMonthTarget) { toast.error('Please select a once-a-month target'); return; }
      if (!(deductPerCutoffNumber > 0)) { toast.error('Please enter a valid Deduct amount'); return; }
    }
    if (isPayrollTwiceMonth && !(payrollDeduct15thNumber > 0 || payrollDeductEndOfMonthNumber > 0)) {
      toast.error('Please enter at least one Payroll deduction amount');
      return;
    }
    if (!startDate) { toast.error('Please select a start date'); return; }
    if (!computedEndDate) { toast.error('Unable to compute end date'); return; }

    const derivedDeductPerCutoff = isPayrollTwiceMonth
      ? (payrollDeduct15thNumber > 0 ? payrollDeduct15thNumber : payrollDeductEndOfMonthNumber)
      : deductPerCutoffNumber;

    const payload = {
      employee_id: String(selectedEmployee.employee_id),
      employee_name: String(selectedEmployee.full_name || '').trim(),
      branch: String(selectedEmployee.branch || '').trim(),
      loan_type: loanType,
      loan_subtype: loanSubtype,
      amount: amountNumber,
      deduct_from: deductFrom,
      deduct_per_cutoff: derivedDeductPerCutoff,
      cutoff_count: cutoffCount,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(computedEndDate, 'yyyy-MM-dd'),
      payroll_frequency: isPayrollSelected ? payrollFrequency : null,
      payroll_once_month_target: isPayrollOnceMonth ? payrollOnceMonthTarget : null,
      payroll_deduct_15th: isPayrollTwiceMonth && payrollDeduct15thNumber > 0 ? payrollDeduct15thNumber : null,
      payroll_deduct_end_of_month: isPayrollTwiceMonth && payrollDeductEndOfMonthNumber > 0 ? payrollDeductEndOfMonthNumber : null
    };

    try {
      const res = editingLoanId
        ? await axiosInstance.put(`/payroll/loans/${editingLoanId}`, payload)
        : await axiosInstance.post('/payroll/loans', payload);
      const savedLoan = res?.data;
      toast.success(editingLoanId ? 'Loan updated' : 'Loan created');
      closeLoanModal();
      if (savedLoan?.id) setSelectedLoanId(savedLoan.id);
      if (typeof refreshLoans === 'function') {
        try { await refreshLoans(); } catch {}
      }
    } catch (error) {
      const msg = error?.response?.data?.detail
        ? (typeof error.response.data.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response.data.detail)
        : 'Failed to create loan';
      toast.error(msg);
    }
  };

  const initialCutoffPeriods = React.useMemo(() => {
    const n = Number(selectedLoan?.cutoff_count);
    return Number.isFinite(n) ? Math.round(n) : 0;
  }, [selectedLoan?.cutoff_count]);

  const remainingCutoffPeriods = React.useMemo(() => {
    if (!selectedLoan?.id) return 0;
    if (remainingPeriodsState !== null && remainingPeriodsState !== undefined) {
      const n = Number(remainingPeriodsState);
      if (Number.isFinite(n)) return Math.max(0, Math.round(n));
    }
    return Math.max(0, initialCutoffPeriods - detectedCutoffCount);
  }, [detectedCutoffCount, initialCutoffPeriods, remainingPeriodsState, selectedLoan?.id]);

  const totalDetectedDeducted = React.useMemo(() => {
    if (totalDeductedState !== null && totalDeductedState !== undefined) {
      const n = Number(totalDeductedState);
      if (Number.isFinite(n)) return n;
    }
    return (Array.isArray(cutoffRows) ? cutoffRows : []).reduce((sum, row) => {
      const n = Number(row?.deducted_amount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [cutoffRows, totalDeductedState]);

  const selectedLoanScheduleMeta = React.useMemo(() => {
    return getPayrollScheduleMeta(selectedLoan || {});
  }, [selectedLoan]);

  const selectedLoanScheduleLabel = React.useMemo(() => {
    return getLoanScheduleSummaryLabel(selectedLoan || {});
  }, [selectedLoan]);

  const openCutoffModal = () => {
    if (!selectedLoan?.id) return;
    const employeeId = String(selectedLoan?.employee_id || '').trim();
    const employeeName = String(selectedLoan?.employee_name || '').trim();
    if (!employeeId && !employeeName) {
      toast.error('Missing employee');
      return;
    }
    setIsCutoffModalOpen(true);
  };

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!selectedLoan?.id || !axiosInstance) {
        setDetectedCutoffCount(0);
        setCutoffRows([]);
        setRemainingBalanceState(null);
        setRemainingPeriodsState(null);
        setTotalDeductedState(null);
        setProjectedEndDateState('');
        return;
      }

      try {
        const res = await axiosInstance.get(`/payroll/loans/${selectedLoan.id}/deductions`);
        const data = res?.data || {};
        const deductions = Array.isArray(data.deductions) ? data.deductions : [];
        const rows = deductions.map((d) => ({
          payroll_period: String(d?.payroll_period || '').trim(),
          start_date: d?.start_date || '',
          end_date: d?.end_date || '',
          deducted_amount: Number(d?.deducted_amount ?? d?.amount) || 0,
          scheduled_amount: Number(d?.scheduled_amount) || 0,
          cycle_status: String(d?.cycle_status || 'deducted').trim().toLowerCase() || 'deducted'
        }));

        if (cancelled) return;
        setCutoffRows(rows);
        setDetectedCutoffCount(rows.filter((row) => row?.cycle_status !== 'skipped').length);
        setRemainingBalanceState(
          (data.remaining_balance === null || data.remaining_balance === undefined)
            ? null
            : Number(data.remaining_balance)
        );
        setRemainingPeriodsState(
          (data.remaining_periods === null || data.remaining_periods === undefined)
            ? null
            : Number(data.remaining_periods)
        );
        setTotalDeductedState(
          (data.total_deducted === null || data.total_deducted === undefined)
            ? null
            : Number(data.total_deducted)
        );
        setProjectedEndDateState(String(data.projected_end_date || '').trim());
      } catch (error) {
        if (cancelled) return;
        setDetectedCutoffCount(0);
        setCutoffRows([]);
        setRemainingBalanceState(null);
        setRemainingPeriodsState(null);
        setTotalDeductedState(null);
        setProjectedEndDateState('');
      }
    };

    run();
    return () => { cancelled = true; };
  }, [
    axiosInstance,
    selectedLoan?.id,
    selectedLoan?.updated_at,
    isCutoffModalOpen
  ]);

  React.useEffect(() => {
    if (!selectedLoan?.id) return;
    if (!selectedLoanIsActive) return;
    if (remainingCutoffPeriods !== 0) return;

    const loanId = String(selectedLoan.id);
    if (autoDeactivatedRef.current?.[loanId]) return;

    const shouldDeactivate = detectedCutoffCount > 0;
    if (!shouldDeactivate) return;

    autoDeactivatedRef.current = { ...(autoDeactivatedRef.current || {}), [loanId]: true };
    handleToggleActive(false);
  }, [
    detectedCutoffCount,
    handleToggleActive,
    remainingCutoffPeriods,
    selectedLoan,
    selectedLoanIsActive,
    totalDetectedDeducted
  ]);

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <div className="flex-[0_0_10%] min-h-[84px]">
        <Card className="glass shadow-lg border-0 h-full">
          <CardContent className="p-3 h-full">
            <div className="h-full flex flex-wrap gap-3 items-end">
              <div className="flex flex-col gap-1 min-w-[240px]">
                <Label>Employee Name</Label>
                <Input
                  value={filters?.employeeName || ''}
                  onChange={(e) => setEmployeeName(e.target.value)}
                  placeholder="Type employee name..."
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>Loan Type</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={toggleGovernment}
                    className={governmentActive ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
                  >
                    Government Loans
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={toggleCompany}
                    className={companyActive ? 'bg-yellow-400 text-black hover:bg-yellow-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
                  >
                    Company Loans
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-1 min-w-[220px]">
                <Label>Branches</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-[160px]">
                    <Select value={filters?.branch || 'all'} onValueChange={setBranch}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All branches</SelectItem>
                        {safeBranches.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="outline" size="sm" className="h-9" onClick={clearFilters}>
                    Clear
                  </Button>
                </div>
              </div>

              <div className="flex-1" />
              <Button type="button" onClick={openNewLoan} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                + New
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex-[1_1_90%] min-h-0 flex gap-3">
        <Card className="glass shadow-lg border-0 w-1/5 min-h-0 flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-200">
              Loan Entries {isLoading ? '' : `(${filteredLoans.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 flex-1 min-h-0 overflow-auto">
            {isLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-300 p-2">Loading...</div>
            ) : filteredLoans.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-300 p-2">No loan entries found.</div>
            ) : (
              <div className="space-y-2">
                {filteredLoans.map((loan) => {
                  const isSelected = String(loan?.id) === String(selectedLoanId);
                  const type = String(loan?.loan_type || '').toLowerCase();
                  const typeLabel = type ? `${type.charAt(0).toUpperCase()}${type.slice(1)}` : '';
                  return (
                    <button
                      key={loan?.id}
                      type="button"
                      onClick={() => setSelectedLoanId(loan?.id)}
                      className={`w-full text-left rounded-md border px-2 py-2 transition-colors ${
                        isSelected
                          ? 'bg-emerald-50 border-emerald-300'
                          : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {loan?.employee_name || 'Unnamed'}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
                        {typeLabel}{typeLabel && loan?.branch ? ' • ' : ''}{loan?.branch || ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass shadow-lg border-0 w-4/5 min-h-0 flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-200">Loan Details</CardTitle>
              {selectedLoan ? (
                <div className="flex items-center gap-2">
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    {selectedLoanIsActive ? 'Activated' : 'Deactivated'}
                  </div>
                  <Switch checked={selectedLoanIsActive} onCheckedChange={handleToggleActive} />
                </div>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 p-0">
            {!selectedLoan ? (
              <div className="p-6 text-sm text-gray-500 dark:text-gray-300">Select a loan entry to view details.</div>
            ) : (
              <div className="h-full min-h-0 flex flex-col">
                <div className={`h-[5%] min-h-[10px] w-full ${selectedLoanIsActive ? 'bg-green-500' : 'bg-red-500'}`} />
                <div className="flex-1 min-h-0 overflow-auto p-6">
                  <div className="grid grid-cols-[2fr_2fr_1fr] gap-x-8 gap-y-4">
                    <div className="col-span-3 flex items-center justify-center">
                      <div className="text-2xl font-semibold text-gray-900 dark:text-white text-center">
                        {selectedLoan.employee_name || '-'}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Branch</div>
                      <div className="text-sm text-gray-900 dark:text-white">{selectedLoan.branch || '-'}</div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Loan Type</div>
                      <div className="text-sm text-gray-900 dark:text-white">{formatLoanTypeLabel(selectedLoan)}</div>
                    </div>

                    <div className="col-start-3 row-start-2 row-span-4 flex flex-col items-center justify-center text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-300">Balance Amount</div>
                      <div className="mt-2 text-2xl font-semibold text-red-600">
                        {formatMoneyLabel(
                          (remainingBalanceState !== null && remainingBalanceState !== undefined)
                            ? remainingBalanceState
                            : selectedLoan.amount
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Start Date</div>
                      <div className="text-sm text-gray-900 dark:text-white">{formatDateLabel(selectedLoan.start_date)}</div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">End Date</div>
                      <div className="text-sm text-gray-900 dark:text-white">{formatDateLabel(projectedEndDateState || selectedLoan.end_date)}</div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Principal Amount</div>
                      <div className="text-sm text-gray-900 dark:text-white">{formatMoneyLabel(selectedLoan.amount)}</div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Deduction Schedule</div>
                      <div className="text-sm text-gray-900 dark:text-white">{selectedLoanScheduleLabel}</div>
                    </div>

                    {selectedLoanScheduleMeta.kind === 'twice_month' ? (
                      <>
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-300">To be deduct every 15th of the month</div>
                          <div className="text-sm text-gray-900 dark:text-white">{formatMoneyLabel(selectedLoan.payroll_deduct_15th)}</div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-300">To be deduct every end of the month</div>
                          <div className="text-sm text-gray-900 dark:text-white">{formatMoneyLabel(selectedLoan.payroll_deduct_end_of_month)}</div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-300">Deductable Amount per Cut-off</div>
                        <div className="text-sm text-gray-900 dark:text-white">{formatMoneyLabel(selectedLoan.deduct_per_cutoff)}</div>
                      </div>
                    )}

                    {selectedLoanScheduleMeta.kind === 'once_month' ? (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-300">Payroll Target</div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {selectedLoan.payroll_once_month_target === '15th' ? 'Every 15th' : 'Every End of Month'}
                        </div>
                      </div>
                    ) : null}

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Remaining Cut-off Periods</div>
                      {selectedLoan.cutoff_count === null || selectedLoan.cutoff_count === undefined ? (
                        <div className="text-sm text-gray-900 dark:text-white">-</div>
                      ) : (
                        <button
                          type="button"
                          onClick={openCutoffModal}
                          className="text-sm text-gray-900 dark:text-white underline underline-offset-2 hover:text-emerald-700"
                        >
                          {String(remainingCutoffPeriods)}
                        </button>
                      )}
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Deducted Amount</div>
                      <div className="text-sm text-gray-900 dark:text-white">{formatMoneyLabel(totalDetectedDeducted)}</div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button type="button" variant="outline" size="icon" onClick={openEditLoan} title="Edit loan">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" onClick={handleDeleteLoan} title="Delete loan" className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={isNewLoanOpen} onOpenChange={(open) => {
        if (open) {
          setIsNewLoanOpen(true);
        } else {
          closeLoanModal();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingLoanId ? 'Edit Loan' : 'New Loan'}</DialogTitle>
            <DialogDescription className="sr-only">Employee loan record form</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <div className="text-sm font-semibold">Employee</div>
              <div className="relative">
                <Input
                  value={employeeQuery}
                  onChange={(e) => {
                    setEmployeeQuery(e.target.value);
                    setSelectedEmployee(null);
                  }}
                  placeholder="Type employee name or ID..."
                />
                {employeeMatches.length > 0 && !selectedEmployee && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-md overflow-hidden">
                    {employeeMatches.map((emp) => (
                      <button
                        key={String(emp?.employee_id)}
                        type="button"
                        onClick={() => handleSelectEmployee(emp)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      >
                        <div className="text-sm font-medium text-gray-900">{emp?.full_name || '-'}</div>
                        <div className="text-xs text-gray-600">
                          {emp?.employee_id ? `ID: ${emp.employee_id}` : ''}{emp?.branch ? ` • ${emp.branch}` : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedEmployee?.branch ? (
                <div className="text-xs text-gray-600">Branch: {selectedEmployee.branch}</div>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-semibold">Loan Amount</div>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-600">₱</div>
                <Input
                  value={amount}
                  onChange={(e) => setAmount(sanitizeNumericInput(e.target.value))}
                  className="pl-8"
                  placeholder="-"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-semibold">Type</div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setLoanType('government');
                    setLoanSubtype('');
                  }}
                  className={loanType === 'government' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
                >
                  Government
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setLoanType('company');
                    setLoanSubtype('');
                  }}
                  className={loanType === 'company' ? 'bg-yellow-400 text-black hover:bg-yellow-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}
                >
                  Company
                </Button>
              </div>

              {typeSubtypes.length > 0 && (
                <div className="space-y-2 pt-2">
                  {typeSubtypes.map((opt) => {
                    const selected = loanSubtype === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setLoanSubtype(opt)}
                        className="w-full flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-gray-50"
                      >
                        <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${selected ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-400 text-transparent'}`}>
                          <Check className="h-4 w-4" />
                        </div>
                        <div className="text-sm text-gray-900">{opt}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-semibold">Deduct from</div>
              <div className="flex gap-3">
                {[
                  { key: 'payroll', label: 'Payroll' },
                  { key: 'mf', label: 'MF' }
                ].map((opt) => {
                  const selected = deductFrom === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => handleSelectDeductFrom(opt.key)}
                      className="flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-gray-50"
                    >
                      <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${selected ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-400 text-transparent'}`}>
                        <Check className="h-4 w-4" />
                      </div>
                      <div className="text-sm text-gray-900">{opt.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {isPayrollSelected ? (
              <div className="space-y-3">
                <div className="text-sm font-semibold">Payroll Frequency</div>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { key: 'once_month', label: 'Once a Month' },
                    { key: 'twice_month', label: 'Twice a Month' }
                  ].map((opt) => {
                    const selected = payrollFrequency === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleSelectPayrollFrequency(opt.key)}
                        className="flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-gray-50"
                      >
                        <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${selected ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-400 text-transparent'}`}>
                          <Check className="h-4 w-4" />
                        </div>
                        <div className="text-sm text-gray-900">{opt.label}</div>
                      </button>
                    );
                  })}
                </div>

                {isPayrollOnceMonth ? (
                  <div className="space-y-3 rounded-md border p-3 bg-gray-50">
                    <div className="text-sm font-semibold">Once a Month Schedule</div>
                    <div className="flex gap-3 flex-wrap">
                      {[
                        { key: '15th', label: 'Every 15th' },
                        { key: 'end_month', label: 'Every End of Month' }
                      ].map((opt) => {
                        const selected = payrollOnceMonthTarget === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setPayrollOnceMonthTarget(opt.key)}
                            className="flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-white"
                          >
                            <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${selected ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-400 text-transparent'}`}>
                              <Check className="h-4 w-4" />
                            </div>
                            <div className="text-sm text-gray-900">{opt.label}</div>
                          </button>
                        );
                      })}
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-semibold">Deduct the amount below every cutoff Payroll</div>
                      <Input
                        value={deductPerCutoff}
                        onChange={(e) => setDeductPerCutoff(sanitizeNumericInput(e.target.value))}
                        placeholder="-"
                      />
                    </div>
                  </div>
                ) : null}

                {isPayrollTwiceMonth ? (
                  <div className="space-y-3 rounded-md border p-3 bg-gray-50">
                    <div className="space-y-2">
                      <div className="text-sm font-semibold">To be deduct every 15th of the month</div>
                      <Input
                        value={payrollDeduct15th}
                        onChange={(e) => setPayrollDeduct15th(sanitizeNumericInput(e.target.value))}
                        placeholder="-"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-semibold">To be deduct every end of the month</div>
                      <Input
                        value={payrollDeductEndOfMonth}
                        onChange={(e) => setPayrollDeductEndOfMonth(sanitizeNumericInput(e.target.value))}
                        placeholder="-"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {isMfSelected ? (
              <div className="space-y-2">
                <div className="text-sm font-semibold">Deduct the amount below every cutoff MF</div>
                <Input
                  value={deductPerCutoff}
                  onChange={(e) => setDeductPerCutoff(sanitizeNumericInput(e.target.value))}
                  placeholder="-"
                />
              </div>
            ) : null}

            <div className="space-y-1">
              <div className="text-sm font-semibold">
                Deductable for {toCutoffLabel(cutoffCount)} cutoff.
              </div>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="text-sm font-semibold">Start Date</div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, 'yyyy-MM-dd') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate || undefined}
                        onSelect={(d) => setStartDate(d || null)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1">
                  <div className="text-sm font-semibold">End Date</div>
                  <div className="h-9 rounded-md border px-3 flex items-center text-sm text-gray-900 bg-gray-50">
                    {computedEndDate ? format(computedEndDate, 'yyyy-MM-dd') : '-'}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeLoanModal}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSaveLoan} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {editingLoanId ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isCutoffModalOpen} onOpenChange={setIsCutoffModalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Cut-off Deductions</DialogTitle>
            <DialogDescription className="sr-only">Cut-off deductions for selected loan</DialogDescription>
          </DialogHeader>

          {(!String(selectedLoan?.employee_id || '').trim() && !String(selectedLoan?.employee_name || '').trim()) ? (
            <div className="text-sm text-gray-600">Missing employee.</div>
          ) : cutoffRows.length === 0 ? (
            <div className="text-sm text-gray-600">No payroll cut-off records detected yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payroll Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount Deducted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cutoffRows.map((row, idx) => (
                  <TableRow key={`${row?.start_date || ''}-${row?.end_date || ''}-${idx}`}>
                    <TableCell>
                      {String(row?.payroll_period || '').trim() || `${formatDateLabel(row?.start_date)} - ${formatDateLabel(row?.end_date)}`}
                    </TableCell>
                    <TableCell>{row?.cycle_status === 'skipped' ? 'Skipped' : 'Deducted'}</TableCell>
                    <TableCell className="text-right">{formatMoneyLabel(row?.deducted_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoansPage;
